import { useCallback, useEffect, useState } from 'react';

import { WizardHeader } from '@bambi/component-library/src/components/Wizard/deprecated/WizardHeader';
import * as Dialog from '@radix-ui/react-dialog';
import { FormProvider, useForm } from 'react-hook-form';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { useTilled } from 'src/common/forms/PaymentMethodCaptureForm/useTilled';
import { useConfirmClose } from 'src/common/modals/RequiresConfirmationModal/RequiresConfirmationModal';
import { ModalOnPointerDownOutsideHandler } from 'src/common/primitives/Modal/ModalOnPointerDownOutsideHandler';

import { setSelectedPayer, setShowPayersModal } from '../payers.slice';
import { assignDefaultFormValues } from './assignDefaultFormValues';
import { PayersFormStep1 } from './PayersFormStep1';
import { PayersFormStep2 } from './PayersFormStep2';

export function PayersDialog() {
  const dispatch = useAppDispatch();
  const { showPayersModal, selectedPayer } = useAppSelector(
    (state) => state.payer
  );
  const defaultFormValues = assignDefaultFormValues(selectedPayer);

  const methods = useForm({
    defaultValues: defaultFormValues,
  });

  useEffect(() => {
    methods.reset(defaultFormValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFormValues.id]);

  const [step, setStep] = useState(1);

  const closeModal = () => {
    dispatch(setShowPayersModal(false));
    dispatch(setSelectedPayer(undefined));
    methods.reset();
    setStep(1);
  };

  const setOpenInternal = (val: boolean) => dispatch(setShowPayersModal(val));

  const { savePaymentMethod, validateForm: validatePaymentForm } =
    useTilled('card');

  const shouldConfirmClose = useCallback(
    () => methods.formState.isDirty,
    [methods.formState.isDirty]
  );

  const [setOpen, onClose] = useConfirmClose({
    setOpen: setOpenInternal,
    shouldConfirmClose,
    onClose: closeModal,
  });

  return (
    <>
      <Dialog.Root
        open={showPayersModal}
        onOpenChange={(newValue) => {
          setOpen(newValue);
          if (!newValue) {
            onClose();
          }
        }}
        data-testid="payers-dialog"
      >
        <Dialog.Content
          className="fixed inset-0 z-20 h-full w-full translate-x-0 overflow-hidden data-[state=open]:animate-tripFormSlideIn data-[state=closed]:animate-tripFormSlideOut"
          onPointerDownOutside={ModalOnPointerDownOutsideHandler}
          aria-describedby={undefined}
        >
          <Dialog.Title className="sr-only" asChild>
            <span>Payers - Modal</span>
          </Dialog.Title>
          <div
            className="pointer-events-auto relative flex h-full flex-col overflow-y-auto bg-white p-4 shadow-xl"
            data-testid="payers-form"
          >
            <WizardHeader
              title={selectedPayer?.id ? 'Edit Payer' : 'Add a New Payer'}
              wizardState={{ step, totalSteps: 2 }}
              setStep={setStep}
            />
            <FormProvider {...methods}>
              <form>
                <div className={step !== 1 ? 'hidden' : ''}>
                  <PayersFormStep1
                    onClose={onClose}
                    setStep={(step) => setStep(step)}
                  />
                </div>
                <div className={step !== 2 ? 'hidden' : ''}>
                  <PayersFormStep2
                    onClose={onClose}
                    savePaymentMethod={savePaymentMethod}
                    validatePaymentForm={validatePaymentForm}
                    onCloseAfterSave={closeModal}
                  />
                </div>
              </form>
            </FormProvider>
          </div>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
}
